import { useState, useEffect } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import logger from "../utils/logger";
import axios from "../utils/axios";
import AuthForm from "../components/AuthForm";
import { AuthContext } from "../components/Layout";
import { CrosswordSessionDto } from "../models/crossword-session-dto";
import { ArchiveBoxXMarkIcon } from "@heroicons/react/24/outline";
import "./SessionChooser.css";

const formatCompletionTime = (startTime: Date, completionTime?: Date) => {
    if (!completionTime) return "Completed";
    
    const start = new Date(startTime);
    const end = new Date(completionTime);
    const diffMs = end.getTime() - start.getTime();
    const diffMinutes = Math.round(diffMs / (1000 * 60));
    
    if (diffMinutes < 60) {
      return `${diffMinutes}m`;
    } else {
      const hours = Math.floor(diffMinutes / 60);
      const minutes = diffMinutes % 60;
      return `${hours}h ${minutes}m`;
    }
  };

const REFRESH_INTERVAL_MS = 5000;

export function SessionChooser() {
  const navigate = useNavigate();
  const { user, onLogin } = useOutletContext<AuthContext>();
  const [sessions, setSessions] = useState<CrosswordSessionDto[]>([]);
  const [showCompleted, setShowCompleted] = useState(false);
  const [showOnlyMine, setShowOnlyMine] = useState(false);
  const [loading, setLoading] = useState(true);
  const [abandoningSessionId, setAbandoningSessionId] = useState<number | null>(null);

  useEffect(() => {
    const fetchSessions = async () => {
      try {
        const response = await axios.get<CrosswordSessionDto[]>(
          `/api/cross/sessions?includeCompleted=${showCompleted}`
        );
        setSessions(response.data);
      } catch (error) {
        logger.error("Error fetching sessions:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSessions();
    const intervalId = setInterval(fetchSessions, REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [showCompleted]);

  if (!user) {
    return <AuthForm onLoginSuccess={onLogin} />;
  }

  if (loading) {
    return <div>Loading sessions...</div>;
  }
  const filteredSessions = sessions
    .filter(
      (session) =>
        (!showOnlyMine || Object.keys(session.users).includes(user?.username ?? "")) &&
        (showCompleted || !session.isCompleted)
    )
    .sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime());

  const joinSession = async (id: number, viewOnly:boolean) => {
    try {
      // Then navigate to the session page
      navigate(`/session/${id}?viewonly=${viewOnly}`);
    } catch (error) {
      logger.error("Error joining session:", error);
      // Optional: Add error notification
    }
  };

  const abandonSession = async (session: CrosswordSessionDto) => {
    if (session.users[user.username] !== "A" || abandoningSessionId !== null) return;

    if (!window.confirm("Abandon this crossword? It will be removed from completion statistics.")) {
      return;
    }

    setAbandoningSessionId(session.id);
    try {
      await axios.post(`/api/cross/session/${session.id}/abandon`);
      setSessions((currentSessions) =>
        currentSessions.map((currentSession) =>
          currentSession.id === session.id
            ? { ...currentSession, isCompleted: true, completionTime: undefined }
            : currentSession
        )
      );
    } catch (error) {
      logger.error("Error abandoning session:", error);
    } finally {
      setAbandoningSessionId(null);
    }
  };

  return (
    <div className="session-chooser">
      <div className="session-header">
        <h1>Available Sessions</h1>
        <div className="filters">
          <label className="show-completed">
            <input
              type="checkbox"
              checked={showCompleted}
              onChange={(e) => setShowCompleted(e.target.checked)}
            />
            Show Completed Sessions
          </label>
          <label className="show-completed">
            <input
              type="checkbox"
              checked={showOnlyMine}
              onChange={(e) => setShowOnlyMine(e.target.checked)}
            />
            Show Only My Sessions
          </label>
        </div>
      </div>

      <div className="sessions-table">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Publisher</th>
              <th>Type</th>
              <th>Setter</th>
              <th>Published</th>
              <th>Status</th>
              <th>Access</th>
              <th>Started</th>
              <th>Participants</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredSessions.map((session) => (
              <tr
                key={session.id}
                className={session.isCompleted ? "completed" : ""}
              >
                <td>{session.name}</td>
                <td>{session.publisherName}</td>
                <td>{session.sourceType}</td>
                <td>{session.setter || "Unknown"}</td>
                <td>
                  {new Date(session.publicationDate ?? "").toLocaleDateString()}
                </td>
                <td>
                  <span
                    className={`status ${
                      session.isCompleted ? "completed" : "active"
                    }`}>
                      {session.isCompleted 
                        ? (session.completionTime ?  "Completed" : "Abandoned")
                        : "Active"}
                        <br/>
                      {session.completionTime 
                        ? formatCompletionTime(session.startTime, session.completionTime):""}
                  </span>
                </td>
                <td>{session.isOpenDoor ? "Open" : "By Request"}</td>
                <td>{new Date(session.startTime).toLocaleString()}</td>
                <td>{Object.keys(session.users).join(", ")}</td>
                <td>
                  { session.isCompleted ? null : (
                  <button
                    className="join-button"
                    disabled={session.isCompleted}
                    onClick={()=>joinSession(session.id,false)}>
                    { session.isOpenDoor
                      ? "Join"
                      : "Request"}
                  </button>)
                  }
                  <button
                    className="view-button"
                    onClick={()=>joinSession(session.id,true)}
                  >
                    View
                  </button>
                  {session.users[user.username] === "A" && !session.isCompleted && (
                    <button
                      className="abandon-session-button"
                      onClick={() => abandonSession(session)}
                      disabled={abandoningSessionId !== null}
                      title="Abandon crossword"
                      aria-label={`Abandon ${session.name}`}
                    >
                      <ArchiveBoxXMarkIcon />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
